import React, { useState, useEffect } from "react";
import axios from "axios";
import SimpleResultsDisplay from "../SimpleResultsDisplay/SimpleResultsDisplay";
import "./InstagramAnalyzer.css";

const InstagramAnalyzer = () => {
  const [instagramUrl, setInstagramUrl] = useState("");
  const [safeSearch, setSafeSearch] = useState(true);
  const [loading, setLoading] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const username = params.get("username");

    if (username) {
      setInstagramUrl(username.replace(/@/g, ""));
    }
  }, []);

  const validateInstagramUrl = (username) => {
    if (!username || typeof username !== "string") return null;
    let cleanUser = username.trim().replace(/@/g, "");
    const fullUrl = `https://instagram.com/${cleanUser}`;
    const instagramPattern =
      /^https?:\/\/(www\.)?instagram\.com\/[a-zA-Z0-9_.]+\/?$/;

    return instagramPattern.test(fullUrl) ? fullUrl : null;
  };

  const cleanInstagramUrl = (url) => {
    const match = url.match(
      /^(https?:\/\/(www\.)?instagram\.com\/[a-zA-Z0-9_.]+)/
    );
    return match ? match[1] : url;
  };

  const makeApiCall = async (url, data) => {
    return await axios.post(url, data, {
      timeout: 300000,
      headers: {
        "Content-Type": "application/json",
      },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResults(null);
    setLoading(true);

    const form = e.target;

    try {
      const formData = new FormData(form);
      const encoded = new URLSearchParams(formData).toString();

      await fetch("/instagram-form.html", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: encoded,
      });

      console.log("Saved to Netlify Forms!");
    } catch (err) {
      console.error("Netlify form save failed:", err);
    }

    if (!instagramUrl.trim()) {
      setError("Instagram URL is required");
      setLoading(false);
      return;
    }

    if (!validateInstagramUrl(instagramUrl.trim())) {
      setError("Please enter a valid Instagram profile URL");
      setLoading(false);
      return;
    }

    const cleanedUrl = cleanInstagramUrl(instagramUrl.trim());
    const requestData = {
      instagram_url: "https://instagram.com/" + cleanedUrl,
      email: userEmail,
      enable_safe_search: true,
      enable_face_filter: true,
      enable_ai_analysis: true,
      enable_metadata_extraction: true,
    };

    try {
      // Try HTTP first (bypasses SSL certificate issues)
      console.log("Trying HTTP endpoint...");
      const response = await makeApiCall(
        "https://api.staging.phishspot.ai/instagram/reverse-search",
        requestData
      );

      console.log("Success with HTTP:", response.data);
      setResults(response.data);
    } catch (httpError) {
      console.log("HTTP failed, trying HTTPS...", httpError.message);

      try {
        // Fallback to HTTPS
        const response = await makeApiCall(
          "https://api.staging.phishspot.ai/instagram/reverse-search",
          requestData
        );

        console.log("Success with HTTPS:", response.data);
        setResults(response.data);
      } catch (httpsError) {
        console.error("Both HTTP and HTTPS failed:", httpsError);

        let errorMessage = "Analysis failed. Please try again.";

        if (httpsError.code === "ERR_CERT_DATE_INVALID") {
          errorMessage =
            "SSL Certificate error. Please use HTTP endpoint or contact API administrator.";
        } else if (httpsError.code === "ERR_NETWORK") {
          errorMessage =
            "Network error. Please check your internet connection or try again later.";
        } else if (httpsError.response?.data?.detail) {
          errorMessage = httpsError.response.data.detail;
        } else if (httpsError.message) {
          errorMessage = httpsError.message;
        }

        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setInstagramUrl("");
    setSafeSearch(true);
    setError(null);
    setResults(null);
  };

  return (
    <div className="instagram-analyzer">
      <div className="container">
        <div className="analyzer-header">
          <h1>Instagram Profile Picture Analyzer</h1>
          <p>
            Analyze Instagram profile pictures with Phishspot advanced reverse
            image search
          </p>
        </div>

        <form
          name="instagram-analysis"
          method="POST"
          data-netlify="true"
          onSubmit={handleSubmit}
          className="analyzer-form"
        >
          <input type="hidden" name="form-name" value="instagram-analysis" />

          <div className="form-group">
            <label htmlFor="instagramUsername">Instagram Username</label>
            <input
              id="instagramUsername"
              name="instagramUsername"
              type="text"
              value={instagramUrl}
              onChange={(e) =>
                setInstagramUrl(e.target.value.replace(/@/g, ""))
              }
              placeholder="@ username"
              className="form-input"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="userEmail">Email (Optional)</label>
            <input
              id="userEmail"
              name="userEmail"
              type="email"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              placeholder="Enter your email"
              className="form-input"
            />
          </div>

          <label className="checkbox-label">
            <input
              type="checkbox"
              name="safeSearch"
              checked={safeSearch}
              onChange={(e) => setSafeSearch(e.target.checked)}
            />
            &nbsp;Enable Safe Search
          </label>

          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !instagramUrl.trim()}
            >
              {loading ? "Analyzing..." : "Analyze Profile"}
            </button>

            {(results || error) && (
              <button
                type="button"
                onClick={handleReset}
                className="btn btn-secondary"
              >
                Reset
              </button>
            )}
          </div>
        </form>

        {loading && (
          <div className="loader-overlay">
            <div className="loader-box">
              <div className="spinner"></div>
              <p className="loader-text">
                Analyzing Instagram...
                <br />
                Please wait while we process.
              </p>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div
            style={{
              backgroundColor: "#ffebee",
              color: "#c62828",
              padding: "15px",
              borderRadius: "8px",
              margin: "20px 0",
            }}
          >
            <strong>Error:</strong> {error}
          </div>
        )}

        {results && (
          <SimpleResultsDisplay results={results} userEmail={userEmail} />
        )}
      </div>
    </div>
  );
};

export default InstagramAnalyzer;
